import { useMemo, useState } from "react";

import {
  ChevronDownIcon,
  cn,
  V2Button,
  V2SingleSelectDropdown,
  V2SingleSelectDropdownItem,
} from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";

import { ROLES } from "app/constants/rbac";

import type {
  RolePicker_roles$key,
  RolePicker_roles$data,
} from "./__generated__/RolePicker_roles.graphql";
import type { RolePickerRefetchQuery } from "./__generated__/RolePickerRefetchQuery.graphql";

type RoleEdge = NonNullable<RolePicker_roles$data["roles"]["edges"]>[number];

export interface RolePickerProps {
  rolesRef: RolePicker_roles$key;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  /** Array of role values that should be disabled/unavailable for selection */
  disabledOptions?: string[];
}

export function RolePicker({
  rolesRef,
  value = "",
  onChange,
  disabled = false,
  className,
  disabledOptions = [],
}: RolePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(value);

  const { data } = usePaginationFragment<
    RolePickerRefetchQuery,
    RolePicker_roles$key
  >(
    graphql`
      fragment RolePicker_roles on Query
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 100 }
        after: { type: "String" }
      )
      @refetchable(queryName: "RolePickerRefetchQuery") {
        roles(first: $first, after: $after)
          @connection(key: "RolePicker_roles") {
          edges {
            node {
              id
              name
              system
            }
          }
        }
      }
    `,
    rolesRef,
  );

  const roleOptions = useMemo(() => {
    if (!data?.roles?.edges) return [];

    // Get the order of roles from ROLES
    const roleOrder = Object.keys(ROLES);

    return (
      data.roles.edges
        .filter((edge): edge is RoleEdge => Boolean(edge?.node))
        .map((edge) => {
          const node = edge.node;
          return {
            key: node.id,
            value: node.name,
            label: node.name,
            description:
              ROLES[node.name as keyof typeof ROLES]?.description.long ||
              "Custom role",
            isSystem: node.system,
            // Add order index for sorting
            orderIndex: roleOrder.indexOf(node.name),
          };
        })
        // Sort by the order in ROLES (if found), otherwise at the end
        .sort((a, b) => {
          if (a.orderIndex === -1) return 1;
          if (b.orderIndex === -1) return -1;
          return a.orderIndex - b.orderIndex;
        })
    );
  }, [data]);

  const selectedOption = roleOptions.find((r) => r.value === selectedRole);

  const handleChange = (roleValue: string) => {
    setSelectedRole(roleValue);
    onChange(roleValue);
  };

  return (
    <div className={cn("w-full flex flex-col gap-[0.375rem]", className)}>
      <p className="text-label-small text-text-secondary">Select role</p>
      <div className="w-full [&>span]:w-full">
        <V2SingleSelectDropdown
          AnchorEle={
            <V2Button
              IconRight={
                <ChevronDownIcon
                  className={cn(
                    "transition-transform shrink-0",
                    isOpen ? "rotate-180" : "",
                  )}
                  size={12}
                />
              }
              aria-label="Select a role"
              className="h-auto min-h-[2.325rem] w-full items-start justify-between rounded-1 border-legacy-navy-L70 bg-background-primary px-3 py-2 font-normal"
              isDisabled={disabled}
              variant="tertiary"
            >
              {selectedOption ? (
                <div className="text-left">
                  <p className="text-sm font-medium">{selectedOption.label}</p>
                  <p className="text-xs text-text-secondary">
                    {selectedOption.description}
                  </p>
                </div>
              ) : (
                <span className="text-sm text-text-secondary">
                  Select a role
                </span>
              )}
            </V2Button>
          }
          onOpenChange={setIsOpen}
          matchTriggerWidth
        >
          {roleOptions.map((role) => (
            <V2SingleSelectDropdownItem
              isDisabled={disabledOptions.includes(role.value)}
              isSelected={selectedRole === role.value}
              key={role.key}
              onPress={() => handleChange(role.value)}
            >
              <div className="w-full">
                <p className="text-sm font-medium">{role.label}</p>
                <p className="text-xs text-text-secondary">
                  {role.description}
                </p>
              </div>
            </V2SingleSelectDropdownItem>
          ))}
        </V2SingleSelectDropdown>
      </div>
    </div>
  );
}
